import type { ReactNode } from 'react';
import { Pencil } from 'lucide-react';

interface InfoCardProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  editable?: boolean;
  onEdit?: () => void;
  editLabel?: string;
  className?: string;
  compact?: boolean;
}

export function InfoCard({
  title,
  icon,
  children,
  editable = false,
  onEdit,
  editLabel = 'Modifier',
  className = '',
  compact = false,
}: InfoCardProps) {
  return (
    <div
      className={`tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm ${
        compact ? 'tw-p-3' : 'tw-p-4 sm:tw-p-5'
      } ${className}`}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          {icon && <span className="tw-text-affinicar-primary">{icon}</span>}
          <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0 tw-uppercase tw-tracking-wider">
            {title}
          </h3>
        </div>
        {editable && onEdit && (
          <button
            onClick={onEdit}
            className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-400 hover:tw-text-affinicar-primary hover:tw-underline tw-transition-colors"
          >
            <Pencil className="tw-w-3 tw-h-3" />
            {editLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  href?: string;
  hide?: boolean;
}

export function InfoRow({ label, value, icon, href, hide = false }: InfoRowProps) {
  if (hide) return null;

  const valueContent = href ? (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="tw-text-affinicar-primary hover:tw-underline tw-transition-colors tw-no-underline"
    >
      {value}
    </a>
  ) : (
    value
  );

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5">
      {icon && <span className="tw-text-gray-400 tw-flex-shrink-0">{icon}</span>}
      <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-1 tw-min-w-0 tw-flex-1">
        <span className="tw-text-xs tw-text-gray-500 tw-flex-shrink-0 sm:tw-w-20">{label}</span>
        <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-break-words tw-min-w-0">{valueContent}</span>
      </div>
    </div>
  );
}
